import { ElementPreview } from "@/components/ElementPreview";
import { CONTACT, IMAGES } from "@/lib/site-data";
import { eur, dstr, vatLabel } from "./shared";

const itemDims = (it) =>
  it.product === "Zäune"
    ? `${it.fence_length || "–"} m · Höhe ${it.fence_height || "–"}`
    : it.product === "Dienstleistung / Montage"
      ? null
      : `${it.width || "–"} × ${it.height || "–"} mm`;

const RED = "#e2001a";

export const InvoiceSheet = ({ invoice }) => {
  const net = invoice.items.reduce((s, it) => s + it.qty * it.unit_price, 0);
  const rate = invoice.vat_rate ?? 19;
  const vat = net * (rate / 100);
  const gross = net + vat;
  const paid = invoice.status === "bezahlt";

  return (
    <div className="print-sheet bg-white text-[#1a1d20]" data-testid="invoice-sheet" style={{ fontFamily: "Manrope, Arial, sans-serif" }}>
      {/* Kopf: Logo + Firmenblock */}
      <div className="flex items-start justify-between px-10 pt-8 pb-5">
        <div style={{ background: "#23272b", borderRadius: 10, padding: "10px 14px" }}>
          <img src={IMAGES.logo} alt="Ringel Fenstertechnik" style={{ height: 78 }} />
        </div>
        <div className="text-right text-xs leading-relaxed text-neutral-600">
          <p className="font-bold text-sm text-[#1a1d20]" style={{ fontFamily: "Outfit, sans-serif" }}>Ringel Fenstertechnik</p>
          <p>Inhaber: {CONTACT.owner}</p>
          <p>Ostkamp 50</p>
          <p>59174 Kamen</p>
          <p className="mt-2">Telefon: {CONTACT.phone}</p>
          <p>E-Mail: {CONTACT.email}</p>
        </div>
      </div>

      <div className="px-10">
        <p className="text-[10px] text-neutral-400 underline mb-3">Ringel Fenstertechnik · Ostkamp 50 · 59174 Kamen</p>
        <div className="flex justify-between gap-10 mb-8">
          <div className="text-sm">
            <p className="font-semibold">{invoice.customer_name}</p>
            {invoice.customer_address && <p className="whitespace-pre-line text-neutral-600">{invoice.customer_address}</p>}
            {invoice.customer_email && <p className="text-neutral-600">{invoice.customer_email}</p>}
          </div>
        </div>

        {/* Meta-Zeile */}
        <div className="flex items-end justify-between mb-6">
          <h1 className="text-3xl font-bold tracking-wide" style={{ fontFamily: "Outfit, sans-serif", color: RED }}>RECHNUNG</h1>
          <table className="text-xs" style={{ borderCollapse: "collapse" }}>
            <tbody>
              {[
                ["Rechnungsnummer:", invoice.number],
                ["Rechnungsdatum:", dstr(invoice.invoice_date || invoice.timestamp)],
                ...(invoice.due_date ? [["Fällig bis:", dstr(invoice.due_date)]] : []),
                ["Bearbeiter:", CONTACT.owner],
              ].map(([k, v]) => (
                <tr key={k}>
                  <td className="pr-4 py-0.5 text-neutral-500">{k}</td>
                  <td className="py-0.5 font-semibold text-right">{v}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="text-sm mb-6">
          Sehr geehrte Damen und Herren,<br />
          für die erbrachten Leistungen berechnen wir Ihnen folgende Positionen:
        </p>

        {/* Positions-Köpfe (Details) */}
        {invoice.items.map((it, i) => (
          <div key={`d${i}`} className="text-xs mb-1.5">
            <p className="font-bold">Positionsname: <span className="font-semibold" style={{ color: RED }}>{it.product}</span></p>
            <p className="text-neutral-500">
              {itemDims(it) && <span className="font-semibold text-neutral-700">{itemDims(it)} · </span>}
              Farbe: {it.color || "–"}
              {Object.entries(it.options || {}).filter(([, v]) => v).map(([k, v]) => ` · ${v}`).join("")}
            </p>
          </div>
        ))}

        {/* Positionstabelle */}
        <table className="w-full text-sm mt-3" style={{ borderCollapse: "collapse" }}>
          <thead>
            <tr className="text-left text-xs uppercase tracking-widest" style={{ color: RED }}>
              <th className="pb-2 pr-3 border-b" style={{ borderColor: RED }}>Pos.</th>
              <th className="pb-2 pr-3 border-b" style={{ borderColor: RED }}>Skizze</th>
              <th className="pb-2 pr-3 border-b" style={{ borderColor: RED }}>Bezeichnung</th>
              <th className="pb-2 pr-3 border-b text-right" style={{ borderColor: RED }}>Menge</th>
              <th className="pb-2 pr-3 border-b text-right" style={{ borderColor: RED }}>Einzelpreis</th>
              <th className="pb-2 border-b text-right" style={{ borderColor: RED }}>Gesamtpreis</th>
            </tr>
          </thead>
          <tbody>
            {invoice.items.map((it, i) => (
              <tr key={i} className="border-b border-neutral-200 align-top">
                <td className="py-4 pr-3 text-neutral-400">{i + 1}</td>
                <td className="py-4 pr-3">
                  <div style={{ width: 104, height: 78, background: "#f2f2f0", borderRadius: 8, padding: 6 }}>
                    <ElementPreview bare productName={it.product} color={it.color} opts={it.options || {}} aspect={it.width && it.height ? it.width / it.height : 1} />
                  </div>
                  {itemDims(it) && <p className="text-[10px] text-neutral-500 mt-1 text-center">{itemDims(it)}</p>}
                </td>
                <td className="py-4 pr-3">
                  <p className="font-semibold">{it.product}</p>
                  <p className="text-neutral-600 text-xs mt-0.5">{it.color}</p>
                </td>
                <td className="py-4 pr-3 text-right">{it.qty}</td>
                <td className="py-4 pr-3 text-right">{eur(it.unit_price)}</td>
                <td className="py-4 text-right font-semibold">{eur(it.qty * it.unit_price)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Summen */}
        <div className="flex justify-end mt-5">
          <table className="text-sm w-72" style={{ borderCollapse: "collapse" }}>
            <tbody>
              <tr>
                <td className="py-1.5 text-neutral-500">Zwischensumme (netto)</td>
                <td className="py-1.5 text-right font-semibold">{eur(net)}</td>
              </tr>
              <tr>
                <td className="py-1.5 text-neutral-500">{vatLabel(rate)}</td>
                <td className="py-1.5 text-right">{eur(vat)}</td>
              </tr>
              <tr className="border-t-2" style={{ borderColor: RED }}>
                <td className="py-2 font-bold">Gesamtbetrag</td>
                <td className="py-2 text-right font-bold text-lg" style={{ color: RED }}>{eur(gross)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Zahlungshinweis / Bezahlt-Vermerk */}
        {paid ? (
          <div data-testid="invoice-paid-note" className="mt-8 text-sm rounded-lg px-4 py-3" style={{ background: "#eafaf1", borderLeft: "3px solid #1e9e5a", color: "#14532d" }}>
            Diese Rechnung wurde am {dstr(invoice.paid_at)} vollständig beglichen. Vielen Dank!
          </div>
        ) : (
          <div data-testid="invoice-payment-note" className="mt-8 text-sm rounded-lg px-4 py-3" style={{ background: "#f6f5f2", borderLeft: `3px solid ${RED}` }}>
            Bitte überweisen Sie den Gesamtbetrag von <b>{eur(gross)}</b> unter Angabe der Rechnungsnummer <b>{invoice.number}</b>
            {invoice.due_date && <> bis zum <b>{dstr(invoice.due_date)}</b></>}.
          </div>
        )}

        {invoice.note && (
          <div className="mt-8 text-sm">
            <p className="text-xs uppercase tracking-widest mb-1" style={{ color: RED }}>Hinweise</p>
            <p className="whitespace-pre-line text-neutral-600">{invoice.note}</p>
          </div>
        )}

        <p className="text-sm mt-8">Mit freundlichen Grüßen<br />{CONTACT.owner}</p>

        <p className="mt-8 text-[10px] text-neutral-400 border-t border-neutral-200 pt-3">
          Ringel Fenstertechnik · Inhaber {CONTACT.owner} · Ostkamp 50 · 59174 Kamen · {CONTACT.phone} · {CONTACT.email}
        </p>
      </div>
    </div>
  );
};
